import React, { FC, useMemo } from 'react';
import { PermissionLevels } from 'common/constants/permissions';
import { getInitialPermissionLevelContext } from 'utils/permission.utils';
import usePermissionContext from 'common/hooks/context/usePermissionContext';
import usePermissionLevel from 'common/hooks/usePermissionLevel';
import PermissionLevelContext, {
  IPermissionLevelContext,
} from 'common/contexts/PermissionLevelContext';

interface IPermissionLevelContextProvider {
  children: JSX.Element;
}

const PermissionLevelContextProvider: FC<IPermissionLevelContextProvider> = ({ children }) => {
  const permissionLevel = usePermissionLevel();
  const pCtx = usePermissionContext();

  const contextValue = useMemo((): IPermissionLevelContext => {
    const ctx = getInitialPermissionLevelContext();
    const isClub = permissionLevel === PermissionLevels.CLIENT;
    const isCorporation = permissionLevel === PermissionLevels.CORPORATE;
    const isPeak = permissionLevel === PermissionLevels.PEAK;

    if (isClub) {
      ctx.LEVEL_BILLING_INFO_VIEW = pCtx.CLIENT_BILLING_INFO_VIEW;
      ctx.LEVEL_BILLING_INFO_EDIT = pCtx.CLIENT_BILLING_INFO_EDIT;
      ctx.LEVEL_PROCESSOR_CONFIG_VIEW = pCtx.CLIENT_PROCESSOR_CONFIGURATION_VIEW;
      ctx.LEVEL_PROCESSOR_CONFIG_EDIT = pCtx.CLIENT_PROCESSOR_CONFIGURATION_EDIT;
      ctx.LEVEL_TAXES_INFO_VIEW = pCtx.CLIENT_TAXES_INFO_VIEW;
      ctx.LEVEL_TAXES_INFO_EDIT = pCtx.CLIENT_TAXES_INFO_EDIT;
      ctx.LEVEL_OTHER_INFO_VIEW = pCtx.CLIENT_OTHER_INFO_VIEW;
      ctx.LEVEL_OTHER_INFO_EDIT = pCtx.CLIENT_OTHER_INFO_EDIT;
    } else if (isCorporation) {
      ctx.LEVEL_SIGN_IN_AS_SUPPORT_USER = pCtx.CORPORATE_SIGN_IN_AS_SUPPORT_USER;
      ctx.LEVEL_TRANSFER_OWNERSHIP = pCtx.CORPORATE_TRANSFER_OWNERSHIP;
      ctx.LEVEL_GENERAL_INFO_VIEW = pCtx.CORPORATE_GENERAL_INFO_VIEW;
      ctx.LEVEL_GENERAL_INFO_EDIT = pCtx.CORPORATE_GENERAL_INFO_EDIT;
      ctx.LEVEL_BANK_INFO_VIEW = pCtx.CORPORATE_BANK_INFO_VIEW;
      ctx.LEVEL_BANK_INFO_EDIT = pCtx.CORPORATE_BANK_INFO_EDIT;
      ctx.LEVEL_BILLING_INFO_VIEW = pCtx.CORPORATE_BILLING_INFO_VIEW;
      ctx.LEVEL_BILLING_INFO_EDIT = pCtx.CORPORATE_BILLING_INFO_EDIT;
      ctx.LEVEL_FEE_RATES_VIEW = pCtx.CORPORATE_FEE_RATES_VIEW;
      ctx.LEVEL_FEE_RATES_EDIT = pCtx.CORPORATE_FEE_RATES_EDIT;
      ctx.LEVEL_PROCESSOR_CONFIG_VIEW = pCtx.CORPORATE_PROCESSOR_CONFIGURATION_VIEW;
      ctx.LEVEL_PROCESSOR_CONFIG_EDIT = pCtx.CORPORATE_PROCESSOR_CONFIGURATION_EDIT;
      ctx.LEVEL_TAXES_INFO_VIEW = pCtx.CORPORATE_TAXES_INFO_VIEW;
      ctx.LEVEL_TAXES_INFO_EDIT = pCtx.CORPORATE_TAXES_INFO_EDIT;
      ctx.LEVEL_INTEGRATION_VIEW = pCtx.CORPORATE_INTEGRATION_VIEW;
      ctx.LEVEL_INTEGRATION_EDIT = pCtx.CORPORATE_INTEGRATION_EDIT;
      ctx.LEVEL_INTEGRATION_CHANGE_STATUS = pCtx.CORPORATE_INTEGRATION_CHANGE_STATUS;
      ctx.LEVEL_OTHER_INFO_VIEW = pCtx.CORPORATE_OTHER_INFO_VIEW;
      ctx.LEVEL_OTHER_INFO_EDIT = pCtx.CORPORATE_OTHER_INFO_EDIT;
      ctx.LEVEL_REQUIRED_FIELDS_VIEW = pCtx.CORPORATE_REQUIRED_FIELDS_VIEW;
      ctx.LEVEL_REQUIRED_FIELDS_EDIT = pCtx.CORPORATE_REQUIRED_FIELDS_EDIT;
      ctx.LEVEL_PAY_PERIOD_SETTINGS_VIEW = pCtx.CORPORATE_PAY_PERIOD_SETTINGS_VIEW;
      ctx.LEVEL_PAY_PERIOD_SETTINGS_CREATE = pCtx.CORPORATE_PAY_PERIOD_SETTINGS_CREATE;
      ctx.LEVEL_PAY_PERIOD_SETTINGS_EDIT = pCtx.CORPORATE_PAY_PERIOD_SETTINGS_EDIT;
      ctx.LEVEL_PAY_PERIOD_VIEW = pCtx.CORPORATE_PAY_PERIOD_VIEW;
      ctx.LEVEL_PAY_PERIOD_EDIT = pCtx.CORPORATE_PAY_PERIOD_EDIT;
      ctx.LEVEL_PAY_PERIOD_CHANGE_STATUS = pCtx.CORPORATE_PAY_PERIOD_CHANGE_STATUS;
    } else if (isPeak) {
      ctx.LEVEL_EMPLOYEE_EDIT = pCtx.PEAK_EMPLOYEE_EDIT;
      ctx.LEVEL_EMPLOYEE_CHANGE_STATUS = pCtx.PEAK_EMPLOYEE_CHANGE_STATUS;

      ctx.LEVEL_ROLE_VIEW = pCtx.PEAK_SECURITY_ROLE_VIEW;
      ctx.LEVEL_ROLE_CREATE = pCtx.PEAK_SECURITY_ROLE_CREATE;
      ctx.LEVEL_ROLE_EDIT = pCtx.PEAK_SECURITY_ROLE_EDIT;
      ctx.LEVEL_ROLE_CHANGE_STATUS = pCtx.PEAK_SECURITY_ROLE_CHANGE_STATUS;

      ctx.LEVEL_POLICY_VIEW = pCtx.PEAK_SECURITY_POLICY_VIEW;
      ctx.LEVEL_POLICY_CREATE = pCtx.PEAK_SECURITY_POLICY_CREATE;
      ctx.LEVEL_POLICY_EDIT = pCtx.PEAK_SECURITY_POLICY_EDIT;
      ctx.LEVEL_POLICY_CHANGE_STATUS = pCtx.PEAK_SECURITY_POLICY_CHANGE_STATUS;

      ctx.LEVEL_HELP_INFORMATION_VIEW = pCtx.PEAK_HELP_INFORMATION_VIEW;
      ctx.LEVEL_HELP_INFORMATION_CREATE = pCtx.PEAK_HELP_INFORMATION_CREATE;
      ctx.LEVEL_HELP_INFORMATION_EDIT = pCtx.PEAK_HELP_INFORMATION_EDIT;
      ctx.LEVEL_HELP_INFORMATION_CHANGE_STATUS = pCtx.PEAK_HELP_INFORMATION_CHANGE_STATUS;

      ctx.LEVEL_SIGN_IN_AS_SUPPORT_USER = pCtx.PEAK_ORGANIZATION_SIGN_IN_AS_SUPPORT_USER;
      ctx.LEVEL_TRANSFER_OWNERSHIP = pCtx.PEAK_ORGANIZATION_TRANSFER_OWNERSHIP;
      ctx.LEVEL_GENERAL_INFO_VIEW = pCtx.PEAK_ORGANIZATION_GENERAL_INFO_VIEW;
      ctx.LEVEL_GENERAL_INFO_EDIT = pCtx.PEAK_ORGANIZATION_GENERAL_INFO_EDIT;
      ctx.LEVEL_BANK_INFO_VIEW = pCtx.PEAK_ORGANIZATION_BANK_INFO_VIEW;
      ctx.LEVEL_BANK_INFO_EDIT = pCtx.PEAK_ORGANIZATION_BANK_INFO_EDIT;
      ctx.LEVEL_BILLING_INFO_VIEW = pCtx.PEAK_ORGANIZATION_BILLING_INFO_VIEW;
      ctx.LEVEL_BILLING_INFO_EDIT = pCtx.PEAK_ORGANIZATION_BILLING_INFO_EDIT;
      ctx.LEVEL_FEE_RATES_VIEW = pCtx.PEAK_ORGANIZATION_FEE_RATES_VIEW;
      ctx.LEVEL_FEE_RATES_EDIT = pCtx.PEAK_ORGANIZATION_FEE_RATES_EDIT;
      ctx.LEVEL_PROCESSOR_CONFIG_VIEW = pCtx.PEAK_ORGANIZATION_PROCESSOR_CONFIGURATION_VIEW;
      ctx.LEVEL_PROCESSOR_CONFIG_EDIT = pCtx.PEAK_ORGANIZATION_PROCESSOR_CONFIGURATION_EDIT;
      ctx.LEVEL_TAXES_INFO_VIEW = pCtx.PEAK_ORGANIZATION_TAXES_INFO_VIEW;
      ctx.LEVEL_TAXES_INFO_EDIT = pCtx.PEAK_ORGANIZATION_TAXES_INFO_EDIT;
      ctx.LEVEL_INTEGRATION_VIEW = pCtx.PEAK_ORGANIZATION_INTEGRATION_VIEW;
      ctx.LEVEL_INTEGRATION_EDIT = pCtx.PEAK_ORGANIZATION_INTEGRATION_EDIT;
      ctx.LEVEL_INTEGRATION_CHANGE_STATUS = pCtx.PEAK_ORGANIZATION_INTEGRATION_CHANGE_STATUS;
      ctx.LEVEL_OTHER_INFO_VIEW = pCtx.PEAK_ORGANIZATION_OTHER_VIEW;
      ctx.LEVEL_OTHER_INFO_EDIT = pCtx.PEAK_ORGANIZATION_OTHER_EDIT;
      ctx.LEVEL_REQUIRED_FIELDS_VIEW = pCtx.PEAK_ORGANIZATION_REQUIRED_FIELDS_VIEW;
      ctx.LEVEL_REQUIRED_FIELDS_EDIT = pCtx.PEAK_ORGANIZATION_REQUIRED_FIELDS_EDIT;
      ctx.LEVEL_PAY_PERIOD_SETTINGS_VIEW = pCtx.PEAK_ORGANIZATION_PAY_PERIOD_SETTINGS_VIEW;
      ctx.LEVEL_PAY_PERIOD_SETTINGS_CREATE = pCtx.PEAK_ORGANIZATION_PAY_PERIOD_SETTINGS_CREATE;
      ctx.LEVEL_PAY_PERIOD_SETTINGS_EDIT = pCtx.PEAK_ORGANIZATION_PAY_PERIOD_SETTINGS_EDIT;
      ctx.LEVEL_PAY_PERIOD_VIEW = pCtx.PEAK_ORGANIZATION_PAY_PERIOD_VIEW;
      ctx.LEVEL_PAY_PERIOD_EDIT = pCtx.PEAK_ORGANIZATION_PAY_PERIOD_EDIT;
      ctx.LEVEL_PAY_PERIOD_CHANGE_STATUS = pCtx.PEAK_ORGANIZATION_PAY_PERIOD_CHANGE_STATUS;
    }

    if (isClub || isCorporation) {
      ctx.LEVEL_EMPLOYEE_EDIT = pCtx.EMPLOYEE_EDIT;
      ctx.LEVEL_EMPLOYEE_CHANGE_STATUS = pCtx.EMPLOYEE_CHANGE_STATUS;

      ctx.LEVEL_ROLE_VIEW = pCtx.SECURITY_ROLE_VIEW;
      ctx.LEVEL_ROLE_CREATE = pCtx.SECURITY_ROLE_CREATE;
      ctx.LEVEL_ROLE_EDIT = pCtx.SECURITY_ROLE_EDIT;
      ctx.LEVEL_ROLE_CHANGE_STATUS = pCtx.SECURITY_ROLE_CHANGE_STATUS;

      ctx.LEVEL_POLICY_VIEW = pCtx.SECURITY_POLICY_VIEW;
      ctx.LEVEL_POLICY_CREATE = pCtx.SECURITY_POLICY_CREATE;
      ctx.LEVEL_POLICY_EDIT = pCtx.SECURITY_POLICY_EDIT;
      ctx.LEVEL_POLICY_CHANGE_STATUS = pCtx.SECURITY_POLICY_CHANGE_STATUS;

      ctx.LEVEL_HELP_INFORMATION_VIEW = pCtx.HELP_INFORMATION_VIEW;
      ctx.LEVEL_HELP_INFORMATION_CREATE = pCtx.HELP_INFORMATION_CREATE;
      ctx.LEVEL_HELP_INFORMATION_EDIT = pCtx.HELP_INFORMATION_EDIT;
      ctx.LEVEL_HELP_INFORMATION_CHANGE_STATUS = pCtx.HELP_INFORMATION_CHANGE_STATUS;
    }

    return ctx;
  }, [pCtx, permissionLevel]);

  return (
    <PermissionLevelContext.Provider value={contextValue}>
      {children}
    </PermissionLevelContext.Provider>
  );
};

export default PermissionLevelContextProvider;
